// srcs/frontend/src/components/proposal/state/ProposalContext.tsx
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  useLazyGetFileQuery,   // GET /files
  useLazyGetDataQuery,   // GET /data
} from '../../../services/solutionAPI.ts';

// ====== Types ======
export type ProposalMode = 'new' | 'detail';

export type CompanyInfo = {
  company_name: string;
  industry: string;
  company_size: string;
};

export type NeedsInfo = {
  problem: string;
  ideal_state: string;
  past_efforts: string;
  deadline: string;
  budget: string;
};

export type SelectedSeedInfo = {
  seed_id: string;
  seed_title: string;
  provider: string;
  tech_domain: string;
  solvable_issues: string[];
  case_studies: string[];
  function_overview?: string;
  differentiation_points?: string;
  implementation_requirements?: string;
  cost_structure?: string;
  maturity_level?: string;
};

export type ProposalState = {
  mode: ProposalMode;

  // 新規は null、/seeds/search の受付後にセット
  sessionId: string | null;
  setSessionId: (id: string | null) => void;

  // Seedsページで選択した seed_id
  selectedSeedId: string | null;
  setSelectedSeedId: (id: string | null) => void;

  // Seedsページで入力/選択（GAP/ROI/Draftの左ペイン表示に利用）
  companyInfo: CompanyInfo | null;
  needsInfo: NeedsInfo | null;
  selectedSeedInfo: SelectedSeedInfo | null;
  setCompanyInfo: (v: CompanyInfo | null) => void;
  setNeedsInfo: (v: NeedsInfo | null) => void;
  setSelectedSeedInfo: (v: SelectedSeedInfo | null) => void;

  // 完了状態（sessionId_seedId 単位）
  gapDoneByKey: Record<string, boolean>;
  setGapDoneForKey: (sessionId: string, seedId: string, done: boolean) => void;
  hasGapForCurrent: boolean;

  roiDoneByKey: Record<string, boolean>;
  setRoiDoneForKey: (sessionId: string, seedId: string, done: boolean) => void;
  hasRoiForCurrent: boolean;

  loading: boolean;
  error: string | null;
};

const Ctx = createContext<ProposalState | undefined>(undefined);

type ProviderProps = {
  mode: ProposalMode;
  sessionIdFromRoute?: string; // detail の場合に使用
  children: React.ReactNode;
};

// ====== Helpers ======
function progressStorageKey(sessionId: string) {
  return `proposal_progress_${sessionId}`;
}
function isFinishedStatus(s: any) {
  const v = String(s ?? '').trim().toLowerCase();
  return v === 'finished' || v === '完了';
}

// Seeds 保存ペイロード（復元用の最小型）
type SeedsExportPayload = {
  data_type: 'seeds';
  session_id: string;
  seed_id: 'NULL';
  company_info?: CompanyInfo;
  needs_info?: NeedsInfo;
  selected_seed_id?: string | null;
  selected_seed_info?: SelectedSeedInfo | null;
  results?: any[];
  updated_at?: number;
};

// Gap/Roi/Draft の最小形（report_md 有無で完了判定の補助）
type GapRoiExportPayload = {
  data_type: 'gap' | 'roi' | 'proposal';
  session_id: string;
  seed_id: string;
  report_md?: string | null;  // gap/roi
  draft_md?: string | null;   // proposal
  updated_at?: number;
};

export const ProposalProvider: React.FC<ProviderProps> = ({ mode, sessionIdFromRoute, children }) => {
  // new: null, detail: URL から
  const [sessionId, _setSessionId] = useState<string | null>(() =>
    mode === 'detail' ? (sessionIdFromRoute ?? null) : null
  );
  const setSessionId = (id: string | null) => { _setSessionId(id); };

  const [selectedSeedId, setSelectedSeedId] = useState<string | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [needsInfo, setNeedsInfo] = useState<NeedsInfo | null>(null);
  const [selectedSeedInfo, setSelectedSeedInfo] = useState<SelectedSeedInfo | null>(null);

  // 完了状態（seedごと）
  const [gapDoneByKey, setGapDoneByKey] = useState<Record<string, boolean>>({});
  const [roiDoneByKey, setRoiDoneByKey] = useState<Record<string, boolean>>({});

  const setGapDoneForKey = (sid: string, seedId: string, done: boolean) => {
    const key = `${sid}_${seedId}`;
    setGapDoneByKey((prev) => ({ ...prev, [key]: done }));
  };
  const setRoiDoneForKey = (sid: string, seedId: string, done: boolean) => {
    const key = `${sid}_${seedId}`;
    setRoiDoneByKey((prev) => ({ ...prev, [key]: done }));
  };

  const [loading, setLoading] = useState(mode === 'detail');
  const [error, setError] = useState<string | null>(null);

  const [getFile] = useLazyGetFileQuery();
  const [getData] = useLazyGetDataQuery();

  // =========================
  // detail ロード時の /files 復元 + /data での完了確認
  // =========================
  useEffect(() => {
    let cancelled = false;
    async function hydrateDetail() {
      if (mode !== 'detail') return;
      if (!sessionId) {
        setError('無効なセッションIDです');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);

        // 1) Seeds の /files を読み、左ペインの基礎情報を復元
        try {
          const seedsName = `seeds_${sessionId}_NULL.json`;
          const res: any = await getFile({ file_name: seedsName }).unwrap();
          const raw = res?.file_context ?? res?.items ?? res?.content ?? res;
          const payload: SeedsExportPayload = typeof raw === 'string' ? JSON.parse(raw) : raw;

          if (payload?.data_type === 'seeds') {
            if (payload.company_info) setCompanyInfo(payload.company_info as CompanyInfo);
            if (payload.needs_info) setNeedsInfo(payload.needs_info as NeedsInfo);
            if (payload.selected_seed_id) setSelectedSeedId(payload.selected_seed_id);
            if (payload.selected_seed_info) setSelectedSeedInfo(payload.selected_seed_info as SelectedSeedInfo);
          }
        } catch {
          // seeds ファイルが無いケースは無視（後続の /data 補完に任せる）
        }

        // 2) GAP/ROI の完了フラグを /data → /files の順で確認して立てる
        const sid = sessionId;
        const seedId = selectedSeedId; // seeds /files で復元済みを期待
        if (sid && seedId) {
          // GAP
          let gapDone = false;
          try {
            const gapData: any = await getData({ session_id: sid, data_type: 'gap', seed_id: seedId }).unwrap();
            if (isFinishedStatus(gapData?.status)) gapDone = true;
          } catch {/* ignore */}
          if (!gapDone) {
            try {
              const gapName = `gap_${sid}_${seedId}.json`;
              const gapFile: any = await getFile({ file_name: gapName }).unwrap();
              const raw = gapFile?.file_context ?? gapFile?.items ?? gapFile?.content ?? gapFile;
              const p: GapRoiExportPayload = typeof raw === 'string' ? JSON.parse(raw) : raw;
              if (p?.data_type === 'gap' && typeof p?.report_md === 'string' && p.report_md.trim().length > 0) {
                gapDone = true;
              }
            } catch {/* ignore */}
          }
          if (gapDone) setGapDoneForKey(sid, seedId, true);

          // ROI
          let roiDone = false;
          try {
            const roiData: any = await getData({ session_id: sid, data_type: 'roi', seed_id: seedId }).unwrap();
            if (isFinishedStatus(roiData?.status)) roiDone = true;
          } catch {/* ignore */}
          if (!roiDone) {
            try {
              const roiName = `roi_${sid}_${seedId}.json`;
              const roiFile: any = await getFile({ file_name: roiName }).unwrap();
              const raw = roiFile?.file_context ?? roiFile?.items ?? roiFile?.content ?? roiFile;
              const p: GapRoiExportPayload = typeof raw === 'string' ? JSON.parse(raw) : raw;
              if (p?.data_type === 'roi' && typeof p?.report_md === 'string' && p.report_md.trim().length > 0) {
                roiDone = true;
              }
            } catch {/* ignore */}
          }
          if (roiDone) setRoiDoneForKey(sid, seedId, true);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? '詳細の復元に失敗しました');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    hydrateDetail();
    return () => { cancelled = true; };
  }, [mode, sessionId, selectedSeedId]); // selectedSeedId は seeds復元でセットされるので依存に含める

  // “現在選択中”の seed に対して完了しているか
  const hasGapForCurrent =
    Boolean(sessionId) &&
    Boolean(selectedSeedId) &&
    Boolean(gapDoneByKey[`${sessionId}_${selectedSeedId}`]);

  const hasRoiForCurrent =
    Boolean(sessionId) &&
    Boolean(selectedSeedId) &&
    Boolean(roiDoneByKey[`${sessionId}_${selectedSeedId}`]);

  const value = useMemo<ProposalState>(
    () => ({
      mode,
      sessionId,
      setSessionId,
      selectedSeedId,
      setSelectedSeedId,
      companyInfo,
      needsInfo,
      selectedSeedInfo,
      setCompanyInfo,
      setNeedsInfo,
      setSelectedSeedInfo,
      gapDoneByKey,
      setGapDoneForKey,
      hasGapForCurrent,
      roiDoneByKey,
      setRoiDoneForKey,
      hasRoiForCurrent,
      loading,
      error,
    }),
    [
      mode,
      sessionId,
      selectedSeedId,
      companyInfo,
      needsInfo,
      selectedSeedInfo,
      gapDoneByKey,
      hasGapForCurrent,
      roiDoneByKey,
      hasRoiForCurrent,
      loading,
      error,
    ]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useProposal() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useProposal must be used within ProposalProvider');
  return ctx;
}